/**
 * Kyrgyzstan phone numbers: one accepted shape out, four accepted shapes in.
 *
 * Numbers arrive pasted out of spreadsheets, CRMs and messages, so they arrive
 * punctuated: `+996 700 12-34-56`, `(0700) 123456`, `996‑700‑123456` with a
 * non-breaking hyphen. All of that is formatting, and formatting is discarded.
 * What is *not* discarded is the shape underneath: a number that is too short,
 * or belongs to another country, is rejected rather than repaired, because the
 * repair would be a guess and the guess would cost money to send.
 *
 * Pure by construction (PLAN §4): no I/O, no DOM, no Node — this file is
 * compiled into the browser bundle as well as the server one, so the number the
 * administrator sees validated on screen is validated by exactly this code.
 */

/** Kyrgyzstan. The only country this panel sends to. */
export const COUNTRY_CODE = '996';

/** Digits after the country code: three of operator, six of subscriber. */
export const NATIONAL_DIGITS = 9;

/**
 * Why a number was refused. A code rather than a sentence: the wording is the
 * UI's business, and this file has no language.
 */
export type PhoneProblem =
  | 'empty'
  | 'invalid-characters'
  | 'unsupported-country'
  | 'wrong-length'
  | 'unknown-format';

export type PhoneResult =
  | { readonly ok: true; readonly phone: string }
  | { readonly ok: false; readonly problem: PhoneProblem };

/**
 * Spaces of every width, brackets, dots, and every dash a word processor or a
 * spreadsheet might have substituted for a hyphen. `\s` already covers the
 * non-breaking and narrow spaces.
 *
 * Stripping punctuation cannot produce a *wrong* number — the digits are
 * unchanged and every length check still applies — so it is tolerant here and
 * strict about the digits themselves.
 */
const FORMATTING = /[\s()[\].‐-―−-]/g;

/**
 * Normalises to `+996XXXXXXXXX`, or explains why it could not.
 *
 * Accepts `+996XXXXXXXXX`, `00996XXXXXXXXX`, `996XXXXXXXXX` and the national
 * `0XXXXXXXXX`. The provider accepts both `996…` and `+996…` (PLAN §6), so the
 * plus is a choice: one canonical form makes duplicate detection a string
 * comparison rather than a rule.
 */
export function normalizePhone(input: string): PhoneResult {
  const cleaned = input.replace(FORMATTING, '');
  if (cleaned === '') return fail('empty');

  // `00` is the international prefix dialled from a landline, and turns up in
  // exported contact lists. It means exactly what `+` means.
  const international = cleaned.startsWith('+') || cleaned.startsWith('00');
  const digits = cleaned.startsWith('+')
    ? cleaned.slice(1)
    : cleaned.startsWith('00')
      ? cleaned.slice(2)
      : cleaned;

  if (!/^[0-9]+$/.test(digits)) return fail('invalid-characters');

  if (digits.startsWith(COUNTRY_CODE)) {
    const national = digits.slice(COUNTRY_CODE.length);
    return national.length === NATIONAL_DIGITS
      ? ok(national)
      : fail('wrong-length');
  }

  // Explicitly international and not Kyrgyzstan: a real number, for someone
  // this panel cannot send to. Worth its own message.
  if (international) return fail('unsupported-country');

  if (digits.startsWith('0')) {
    const national = digits.slice(1);
    return national.length === NATIONAL_DIGITS ? ok(national) : fail('wrong-length');
  }

  // Long enough to be a foreign number written without a plus; short enough to
  // be a subscriber number with the operator code missing. Neither can be
  // repaired without guessing.
  return digits.length >= COUNTRY_CODE.length + NATIONAL_DIGITS
    ? fail('unsupported-country')
    : fail('unknown-format');
}

export function isValidPhone(input: string): boolean {
  return normalizePhone(input).ok;
}

/**
 * `+996 700 123 456` — for reading, never for sending or comparing. Anything
 * that is not a normalised number is returned untouched, so a display helper
 * can never quietly mangle a value it did not recognise.
 */
export function formatPhone(phone: string): string {
  const match = /^\+996(\d{3})(\d{3})(\d{3})$/.exec(phone);
  if (match === null) return phone;
  return `+${COUNTRY_CODE} ${match[1]} ${match[2]} ${match[3]}`;
}

function ok(national: string): PhoneResult {
  return { ok: true, phone: `+${COUNTRY_CODE}${national}` };
}

function fail(problem: PhoneProblem): PhoneResult {
  return { ok: false, problem };
}
